import { useState, useEffect, useCallback } from 'react';
import { clearRememberedSession } from '../utils/authStorage';

// Define User interface locally to avoid import issues
export interface User {
  id: number;
  email: string;
  name: string;
  role_id: number;
  company_id?: number;
  created_at: string;
  updated_at: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  isLoading: boolean; // Alias for loading
  error: string | null;
  userRole: number | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: Partial<User>) => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
}

export const useAuth = (): AuthState & AuthActions => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<number | null>(null);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = sessionStorage.getItem('user');
        const token = sessionStorage.getItem('token');
        const loggedOut = sessionStorage.getItem('logged_out');
        
        // If user explicitly logged out, don't auto-authenticate
        if (loggedOut === 'true') {
          setUser(null);
          setUserRole(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        if (storedUser && token) {
          const userData = JSON.parse(storedUser);
          
          // Validate that role_id exists and is a valid number
          // NEVER default to role 1 (Owner) — that grants maximum permissions
          const parsedRoleId = typeof userData.role_id === 'number' ? userData.role_id : null;
          if (!parsedRoleId || parsedRoleId < 1 || parsedRoleId > 4) {
            console.error('⚠️ Invalid or missing role_id in stored user data:', userData.role_id);
            // Clear invalid session and force re-login
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('token');
            setUser(null);
            setUserRole(null);
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }
          
          // Ensure we have all required User fields
          const user: User = {
            id: userData.id || 0,
            email: userData.email || '',
            name: userData.name || 'User',
            role_id: parsedRoleId,
            company_id: userData.company_id,
            created_at: userData.created_at || new Date().toISOString(),
            updated_at: userData.updated_at || new Date().toISOString()
          };
          setUser(user);
          setUserRole(user.role_id);
          setIsAuthenticated(true);
        } else {
          // No stored user or token - user is not authenticated
          console.log('⚠️ No session data found, user not authenticated');
          setUser(null);
          setUserRole(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        // Clear invalid data
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
        setUser(null);
        setUserRole(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to prevent race conditions
    const timer = setTimeout(initializeAuth, 100);
    return () => clearTimeout(timer);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    console.log('⚠️ useAuth.login() is deprecated. Use useLoginFun from api.ts instead.');
    console.log('This function should not be called. Login flow should use useLoginFun hook.');
    return false;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setUserRole(null);
    setIsAuthenticated(false);
    setError(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    // Signing out must also drop the remembered copy, otherwise the next boot
    // would restore the session the user just ended.
    clearRememberedSession();
    // Set logged out flag to prevent auto-login
    sessionStorage.setItem('logged_out', 'true');
  }, []);

  const register = useCallback(async (userData: Partial<User>): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:7002/api';
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      const data = await response.json();

      if (!response.ok || !data.status) {
        setError(data.message || 'Registration failed');
        return false;
      }

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const token = sessionStorage.getItem('token');
      const refreshTkn = sessionStorage.getItem('refreshToken');
      if (!token) {
        return false;
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:7002/api';
      const response = await fetch(`${apiUrl}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ refreshToken: refreshTkn }),
      });
      const data = await response.json();

      if (!response.ok || !data.status) {
        logout();
        return false;
      }

      // Update stored tokens
      if (data.token) {
        sessionStorage.setItem('token', data.token);
      }
      if (data.refreshToken) {
        sessionStorage.setItem('refreshToken', data.refreshToken);
      }

      return true;
    } catch (err) {
      logout();
      return false;
    }
  }, [logout]);

      return {
        user,
        isAuthenticated,
        loading,
        isLoading: loading, // Alias for loading
        error,
        userRole,
        login,
        logout,
        register,
        refreshToken,
      };
};